import type {
  CartV1,
  CartWithPrivateFieldsV1,
  ICurrentCartService,
  ILeanGQLCartModel,
  IMiniCartLeanLineItemModel,
  LineItemWithPrivateFieldsV1,
} from './types';
import { getImageUrl } from '@wix/sdk/media';
import { guidToInt } from '../../utils/utils';
import {
  getCustomTextFields,
  getOptionsSelectionsValues,
} from './getLeanGQLCartFromCart';

export async function getLeanGQLCartFromCartV1(
  currentCartService: ICurrentCartService,
  cart?: CartV1,
): Promise<ILeanGQLCartModel> {
  cart = cart ?? (await currentCartService.getCurrentCartV1());
  return mapCartToLeanGQLCartModelV1(cart as CartWithPrivateFieldsV1);
}

function mapLineItemToProductV1(lineItem: LineItemWithPrivateFieldsV1) {
  const image = lineItem.image ? getImageUrl(lineItem.image) : undefined;
  const media = image
    ? {
        url: /* istanbul ignore next */ image.id ?? image.filename,
        width: image.width,
        height: image.height,
        mediaType: 'PHOTO',
      }
    : { height: 0, width: 0, mediaType: 'PHOTO', url: '' };
  return {
    id: lineItem.catalogReference?.catalogItemId,
    name: lineItem.productName?.translated ?? undefined,
    price: lineItem.priceBeforeDiscounts?.amount
      ? Number(lineItem.priceBeforeDiscounts.amount)
      : 0,
    media: [media],
    weight: lineItem.physicalProperties?.weight ?? 0,
    productType:
      lineItem.itemType?.preset?.toLowerCase() ??
      lineItem.itemType?.custom?.toLowerCase(),
    customTextFields: getCustomTextFields(lineItem.catalogReference?.options),
  };
}

function mapCartToLeanGQLCartModelV1(
  cart: CartWithPrivateFieldsV1,
): ILeanGQLCartModel {
  const coupon = cart.appliedDiscounts?.find(
    (discount: CartWithPrivateFieldsV1['appliedDiscounts'][0]) =>
      discount.coupon,
  )?.coupon;
  const appliedDiscounts = mapLineItemIdToRulesV1(cart.appliedDiscounts);
  return {
    cartId: cart._id,
    items: (cart.lineItems ?? []).map((lineItem: LineItemWithPrivateFieldsV1) =>
      mapLineItemToLeanLineItemModelV1(
        lineItem,
        appliedDiscounts[lineItem._id!],
      ),
    ),
    convertedTotals: {
      itemsTotal: cart.subtotal?.convertedAmount
        ? Number(cart.subtotal.convertedAmount)
        : undefined,
      formattedItemsTotal: cart.subtotal?.formattedConvertedAmount,
      discount: cart.discount?.convertedAmount
        ? Number(cart.discount.convertedAmount)
        : undefined,
      subTotal: cart.subtotal?.convertedAmount
        ? Number(cart.subtotal.convertedAmount)
        : undefined,
      shipping: 0,
      tax: 0,
      total: cart.subtotalAfterDiscounts?.convertedAmount
        ? Number(cart.subtotalAfterDiscounts.convertedAmount)
        : undefined,
    },
    convertedCurrencyFormat: { code: cart.conversionCurrency },
    buyerNote: cart.buyerNote ?? null,
    appliedCoupon: coupon
      ? {
          couponId: coupon._id,
          code: coupon.code,
          name: coupon.code,
          convertedDiscountValue: cart.discount?.formattedConvertedAmount,
        }
      : undefined,
  };
}

function mapLineItemToLeanLineItemModelV1(
  lineItem: LineItemWithPrivateFieldsV1,
  appliedDiscounts: string[],
): IMiniCartLeanLineItemModel {
  const fileType = lineItem.digitalFile?.fileType?.toLowerCase();
  const comparePrice =
    lineItem.fullPrice?.formattedConvertedAmount !==
    lineItem.price?.formattedConvertedAmount
      ? lineItem.fullPrice
      : undefined;
  const formattedPriceBeforeDiscount =
    lineItem.priceBeforeDiscounts?.formattedConvertedAmount !==
    lineItem.price?.formattedConvertedAmount
      ? lineItem.priceBeforeDiscounts?.formattedConvertedAmount
      : '';

  const product = mapLineItemToProductV1(lineItem);

  return {
    cartItemId: guidToInt(lineItem._id!),
    quantity: lineItem.quantity,
    inventoryQuantity: lineItem.availability?.quantityAvailable ?? null,
    productId: lineItem.catalogReference?.catalogItemId,
    catalogAppId: lineItem.catalogReference?.appId,
    optionsSelectionsValues: getOptionsSelectionsValues(
      lineItem.descriptionLines ?? [],
      product.customTextFields,
    ),
    product,
    sku: lineItem.physicalProperties?.sku ?? '',
    convertedPrices: {
      formattedPrice: lineItem.price?.formattedConvertedAmount,
      formattedComparePrice: comparePrice?.formattedConvertedAmount ?? '',
      formattedPriceBeforeDiscount,
      totalPrice: lineItem.lineItemPrice?.convertedAmount
        ? Number(lineItem.lineItemPrice.convertedAmount)
        : 0,
    },
    comparePrice: comparePrice?.amount ? Number(comparePrice.amount) : 0,
    discountRules: appliedDiscounts
      ? appliedDiscounts.map((name) => ({ name }))
      : [],
    urlPart: lineItem.url?.split('/').pop(),
    digitalProductFileItems: fileType ? [{ fileType }] : [],
    ...product,
    media: product.media[0],
  };
}

function mapLineItemIdToRulesV1(
  appliedDiscounts: CartWithPrivateFieldsV1['appliedDiscounts'],
): Record<string, string[]> {
  if (!appliedDiscounts) {
    return {};
  }
  return appliedDiscounts.reduce(
    (
      acc: { [id: string]: string[] },
      discount: CartWithPrivateFieldsV1['appliedDiscounts'][0],
    ) => {
      if (!discount.discountRule) {
        return acc;
      }
      discount.lineItemIds?.forEach((lineItemId: string) => {
        acc[lineItemId] = acc[lineItemId] ?? [];
        acc[lineItemId].push(discount.discountRule!.name.translated);
      });
      return acc;
    },
    {},
  );
}
